import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { customersAPI } from '../../api';
import { AUTOCOMPLETE_MIN_CHARS, AUTOCOMPLETE_DEBOUNCE_MS } from '../../constants';

// Cash Sale/Refund customer name autocomplete - mirrors ProductAutocomplete's
// debounce/cancel pattern, but simpler: one field, no auto-filling other
// fields on selection.
const CustomerAutocomplete = ({ value, onChange, label, placeholder, size = 'small', sx }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value, inputValue]);

  const fetchOptions = async (searchTerm) => {
    if (searchTerm.length < AUTOCOMPLETE_MIN_CHARS) {
      setOptions([]);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await customersAPI.autofill({ customer: searchTerm });
      setOptions(response.data || []);
    } catch (error) {
      if (error.name === 'CanceledError') {
        return;
      }
      console.error('Customer autocomplete error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (onChange) {
      onChange(newInputValue);
    }

    if (reason === 'input') {
      debounceTimerRef.current = setTimeout(() => {
        fetchOptions(newInputValue);
      }, AUTOCOMPLETE_DEBOUNCE_MS);
    } else if (reason === 'clear') {
      setOptions([]);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object' && onChange) {
      onChange(newValue.customer || '');
    }
  };

  const getOptionLabel = (option) => (typeof option === 'string' ? option : option.customer || '');

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => x}
      size={size}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CustomerAutocomplete;
